import { forwardRef } from 'react';
import MuiChip, {
  ChipProps as MuiChipProps,
  ChipPropsColorOverrides,
  ChipPropsSizeOverrides,
} from '@mui/material/Chip';
import { OverridableStringUnion } from '@mui/types';
import { styled } from '@mui/material';
import palette from '../theme/palette';

declare module '@mui/material/Chip' {
  export interface ChipPropsColorOverrides {
    internal: true;
    default: true;
  }

  export interface ChipPropsSizeOverrides {
    /**
     * @deprecated - uses medium size
     */
    big: true;
  }
}

export interface ChipProps extends MuiChipProps {
  size?: OverridableStringUnion<'small' | 'medium', ChipPropsSizeOverrides>;

  /**
   * Color of the Chip.
   */
  color: OverridableStringUnion<
    'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'internal' | 'default',
    ChipPropsColorOverrides
  >;

  /**
   * Chip positioned over the left top corner.
   */
  hangout?: boolean;
}

const StyledChip = styled(MuiChip, {
  name: 'O2Chip',
  shouldForwardProp: (propName) => propName !== 'hangout',
})<ChipProps>(({ hangout }) => ({
  ...(hangout && {
    position: 'absolute',
    top: 0,
    left: '24px',
    transform: 'translateY(-50%)',
    maxWidth: '80%',
    '&.MuiChip-colorDefault:hover': {
      backgroundColor: palette.common.white,
    },
    '&.MuiChip-outlinedInternal, &.MuiChip-outlinedInternal:hover': {
      backgroundColor: palette.background.internal,
    },
  }),
}));

const Chip = forwardRef<HTMLDivElement, ChipProps>(({ color, ...props }, ref) => {
  return <StyledChip ref={ref} color={color} {...props} />;
});

Chip.displayName = 'Chip';

export default Chip;
